import { makeStyles } from "tss-react/mui";
import { getNestedValue, parseFieldValue } from "@miplatform/utils";
import { Box } from "@mui/material";
import { DataGridPremium, gridClasses } from "@mui/x-data-grid-premium";
import { MiPrintTemplate } from "./MiPrintTemplate";

const generateColumns = (fields, classes) =>
  fields.map(({ label, field = "", renderCell, ...props }) => {
    const customRenderCell = !field && !renderCell ? () => null : renderCell;

    return {
      field,
      headerName: label,
      renderCell: customRenderCell,
      valueGetter: customRenderCell
        ? undefined
        : (_value, row) =>
            parseFieldValue(
              getNestedValue(row, field),
              label.toLowerCase().includes("date") ? "--/--/--" : undefined,
              undefined,
              { isCurrency: field === "variance", currency: "USD" }
            ),
      headerClassName: classes.tableHeaderCell,
      cellClassName: classes.tableCell,
      ...props,
    };
  });

export const MiPrintTable = ({
  data = [],
  columns,
  columnHeaderHeight = 24,
  rowHeight = 58,
  account,
  settings,
}) => {
  const { classes } = useStyles({ cellMinHeight: rowHeight });

  const rows = data.map((item, index) => ({
    id: item?.id || index,
    ...item,
  }));

  const gridColumns = generateColumns(columns, classes);

  const ROWS_PER_PAGE_FIRST = 12;
  const ROWS_PER_PAGE_SUBSEQUENT = 14;

  const pages = [];
  let currentRow = 0;

  while (currentRow < rows.length) {
    const isFirstPage = pages.length === 0;
    const limit = isFirstPage ? ROWS_PER_PAGE_FIRST : ROWS_PER_PAGE_SUBSEQUENT;

    const pageRows = rows.slice(currentRow, currentRow + limit);

    pages.push(
      <MiPrintTemplate
        key={`page-${pages.length}`}
        hideFooter
        hideHeader={!isFirstPage}
        account={account}
        orientation={settings?.orientation}
        headerFields={[{ label: "Total Records", value: rows.length }]}
      >
        <Box className={classes.tableContainer}>
          <DataGridPremium
            hideFooter
            rows={pageRows}
            className={classes.dataGrid}
            columns={gridColumns}
            columnHeaderHeight={columnHeaderHeight}
            {...settings?.gridProps}
            hideFooterPagination
            pagination={false}
            disableVirtualization
          />
        </Box>
      </MiPrintTemplate>
    );

    currentRow += limit;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>{pages}</Box>
  );
};

const useStyles = makeStyles()((theme, { cellMinHeight }) => ({
  dataGrid: {
    borderRadius: 0,
    width: "100%",
    "*": {
      overflow: "hidden !important",
    },
  },
  tableContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    [`.${gridClasses.columnHeader}`]: {
      backgroundColor: theme.palette.grey.lightAccent,
    },
    [`.${gridClasses.cell}.grey`]: {
      backgroundColor: theme.palette.grey["100"],
    },
    [`.${gridClasses.cell}.blue`]: {
      backgroundColor: theme.palette.blue.light,
      color: theme.palette.blue.dark,
    },
    [`.${gridClasses.cell}.bold`]: {
      fontWeight: theme.fontWeight.black,
    },
  },
  tableHeaderCell: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.fontWeight.medium,
  },
  tableCell: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: theme.typography.pxToRem(13.5),
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "unset",
    wordBreak: "break-word",
    minHeight: cellMinHeight,
    borderRight: `1px solid ${theme.palette.grey.accent}`,
  },
}));
